import { useState } from 'react';
import { useParams } from 'react-router';
import { paths } from '../app/paths';
import { prepareBroadcastImage } from '../app/prepareImage';
import {
  useBroadcastImage,
  useBroadcastLanding,
  useBroadcasts,
  useCreateBroadcast,
  useDeleteBroadcast,
  useRemoveBroadcastImage,
  useSendBroadcast,
  useSession,
  useTestBroadcast,
  useUpdateBroadcast,
  type BroadcastDraftValues,
} from '../api/queries';
import { asyncState, messageFor } from './asyncState';
import type { BroadcastSendReportDto } from '@pickem/shared';
import * as screens from '../ui/screens/broadcast';

/**
 * The broadcast composer.
 *
 * The state worth explaining is `draftId`. A draft has to exist on the server before an
 * image can be attached to it or a test sent from it, because both are addressed by id —
 * so "New" creates an empty row immediately rather than accumulating a form in memory and
 * saving it all at the end. The cost is the occasional abandoned draft, which is visible
 * in the list and one tap to delete. The alternative was an upload endpoint that took
 * image bytes with no home to put them in.
 */
export function CommishBroadcastRoute() {
  const session = useSession();
  const leagueId = session.data?.activeLeagueId ?? null;

  const list = useBroadcasts(leagueId);
  const create = useCreateBroadcast(leagueId);
  const update = useUpdateBroadcast(leagueId);
  const uploadImage = useBroadcastImage(leagueId);
  const removeImage = useRemoveBroadcastImage(leagueId);
  const remove = useDeleteBroadcast(leagueId);
  const test = useTestBroadcast();
  const send = useSendBroadcast(leagueId);

  const [draftId, setDraftId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sendReport, setSendReport] = useState<BroadcastSendReportDto | null>(null);
  // Local preview of an image still uploading, so the picture appears the moment it is
  // chosen rather than after the round trip.
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);

  const draft = list.data?.find((b) => b.id === draftId) ?? null;

  const clear = () => {
    setActionError(null);
    setSendReport(null);
  };

  const fail = (error: unknown) => setActionError(messageFor(error));

  const emptyDraft: BroadcastDraftValues = {
    title: 'Untitled',
    body: 'Write the message here.',
    landingKicker: null,
    landingHeadline: null,
    landingBody: null,
    targetPath: null,
  };

  return (
    <screens.CommishBroadcast
      {...asyncState(list)}
      broadcasts={list.data ?? []}
      draft={draft}
      isSaving={create.isPending || update.isPending}
      isUploadingImage={uploadImage.isPending || removeImage.isPending}
      pendingImageUrl={pendingImageUrl}
      isTesting={test.isPending}
      isSending={send.isPending}
      sendReport={sendReport}
      actionError={actionError}
      landingUrlFor={(path) => `${window.location.origin}${path}`}
      onNewDraft={() => {
        clear();
        create.mutate(emptyDraft, { onSuccess: (row) => setDraftId(row.id), onError: fail });
      }}
      onEditDraft={(id) => {
        clear();
        setDraftId(id);
      }}
      onCloseDraft={() => {
        clear();
        setDraftId(null);
        setPendingImageUrl(null);
      }}
      onSaveDraft={(values) => {
        if (!draftId) return;
        clear();
        update.mutate({ id: draftId, values }, { onError: fail });
      }}
      onChooseImage={(file) => {
        if (!draftId) return;
        clear();
        prepareBroadcastImage(file).then(
          (prepared) => {
            setPendingImageUrl(prepared.previewUrl);
            uploadImage.mutate(
              { id: draftId, mimeType: prepared.mimeType, data: prepared.data },
              {
                // Cleared on success too: by then the list holds the real URL, and keeping
                // a data: URI of the same image would only pin it in memory.
                onSuccess: () => setPendingImageUrl(null),
                onError: (error: unknown) => {
                  setPendingImageUrl(null);
                  fail(error);
                },
              },
            );
          },
          fail,
        );
      }}
      onRemoveImage={() => {
        if (!draftId) return;
        clear();
        setPendingImageUrl(null);
        removeImage.mutate(draftId, { onError: fail });
      }}
      onTest={() => {
        if (!draftId) return;
        clear();
        test.mutate(draftId, {
          onSuccess: (result) =>
            setSendReport({
              broadcast: draft!,
              delivered: result.delivered,
              recipients: 1,
              note: result.note,
            }),
          onError: fail,
        });
      }}
      onSend={() => {
        if (!draftId) return;
        clear();
        send.mutate(draftId, { onSuccess: setSendReport, onError: fail });
      }}
      onDelete={(id) => {
        clear();
        remove.mutate(id, {
          onSuccess: () => {
            if (draftId === id) setDraftId(null);
          },
          onError: fail,
        });
      }}
    />
  );
}

/**
 * The public landing page.
 *
 * Outside the auth guard on purpose — someone tapping a notification from a lock screen
 * should land on the page it promised, whether or not that browser still holds a session.
 * The slug is the access control, and the server refuses to resolve one belonging to a
 * draft, so nothing leaks before it is sent.
 */
export function BroadcastLandingRoute() {
  const { slug } = useParams<{ slug: string }>();
  const landing = useBroadcastLanding(slug);

  return (
    <screens.BroadcastLanding
      {...asyncState(landing)}
      landing={landing.data ?? null}
      homeHref={paths.root}
    />
  );
}
